// Failure screen — no mockup counterpart (kiosk.jsx draws only the happy
// path); composed in the novem language mirroring SuccessScreen's grammar
// on the cream surface with spec-44 danger tones.

import { useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Classification } from "@novemai/error-envelope";

export interface FailureScreenProps {
  /** i18n key of the user-facing message (errors.code.* namespace). */
  readonly messageKey: string;
  /** Spec-58 classification, when known — powers the mid-tier fallback. */
  readonly classification: Classification | undefined;
  /** Retry guidance carried from the canonical error envelope. */
  readonly retryable?: boolean | undefined;
  readonly retryAfterMs?: number | undefined;
  /** Safe support reference carried from envelope.meta.trace_id. */
  readonly traceId?: string | undefined;
  readonly autoReturnSeconds: number;
  readonly onRetry: () => void;
  readonly onHome: () => void;
}

function cooldownMs(retryable: boolean | undefined, retryAfterMs: number | undefined): number {
  return retryable === true &&
    typeof retryAfterMs === "number" &&
    Number.isFinite(retryAfterMs) &&
    retryAfterMs > 0
    ? retryAfterMs
    : 0;
}

export function FailureScreen({
  messageKey,
  classification,
  retryable,
  retryAfterMs,
  traceId,
  autoReturnSeconds,
  onRetry,
  onHome,
}: FailureScreenProps): React.ReactElement {
  const { t } = useTranslation();
  const retryCooldownMs = cooldownMs(retryable, retryAfterMs);
  const [retryRemainingMs, setRetryRemainingMs] = useState(() => retryCooldownMs);

  useEffect(() => {
    setRetryRemainingMs(retryCooldownMs);
    if (retryCooldownMs === 0) return;

    const retryAt = Date.now() + retryCooldownMs;
    const updateRemaining = (): void => {
      setRetryRemainingMs(Math.max(0, retryAt - Date.now()));
    };
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [retryCooldownMs]);

  // Spec-58 3-tier fallback chain: errors.code.<code> →
  // errors.classification.<classification>.fallback → errors.unknown.fallback
  const classificationFallbackKey =
    classification !== undefined
      ? `errors.classification.${classification}.fallback`
      : "errors.unknown.fallback";
  const retryLocked = retryCooldownMs > 0 && retryRemainingMs > 0;
  const retryWaitSeconds = Math.ceil(retryRemainingMs / 1000);
  const supportMailtoHref =
    classification === "internal"
      ? `mailto:?subject=${encodeURIComponent(t("kiosk.failure.support_email_subject"))}&body=${encodeURIComponent(
          traceId === undefined
            ? t("kiosk.failure.support_email_body_without_trace")
            : t("kiosk.failure.support_email_body", { traceId }),
        )}`
      : undefined;

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-16 pb-10">
      <div
        aria-hidden="true"
        className="mb-[30px] flex h-[148px] w-[148px] items-center justify-center rounded-full bg-[var(--color-danger-100)]"
      >
        <TriangleAlert size={76} strokeWidth={1.8} className="text-[var(--state-danger)]" />
      </div>
      <h1 className="m-0 text-center font-serif text-[clamp(40px,5vw,64px)] font-normal tracking-[-0.015em] text-[var(--fg-primary)]">
        {t("kiosk.failure.title")} <em className="italic">{t("kiosk.failure.title_em")}</em>
      </h1>
      <p role="alert" className="mt-4 max-w-[640px] text-center font-serif text-[22px] text-[var(--fg-secondary)]">
        {/* Key-array fallback (spec 58): backend codes outside the kiosk
            bundle fall through to the classification's generic fallback,
            then to errors.unknown.fallback — never a raw key string to the
            shop-floor user. */}
        {t([messageKey, classificationFallbackKey, "errors.unknown.fallback"])}
      </p>

      <div className="mt-10 flex items-center gap-4">
        <button
          type="button"
          onClick={onHome}
          className="cursor-pointer rounded-full border border-[var(--border-default)] bg-[var(--bg-raised)] px-11 py-4 font-sans text-[17px] font-medium text-[var(--fg-secondary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:bg-[var(--bg-subtle)]"
        >
          {t("kiosk.failure.home")}
        </button>
        <button
          type="button"
          onClick={onRetry}
          disabled={retryLocked}
          className="cursor-pointer rounded-full border-0 bg-[var(--accent)] px-11 py-4 font-sans text-[17px] font-semibold text-[var(--fg-on-accent)] shadow-[var(--shadow-md)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:bg-[var(--accent-active)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("kiosk.failure.retry")}
        </button>
      </div>

      {retryLocked && (
        <p role="status" className="mt-4 font-sans text-[14px] text-[var(--fg-muted)]">
          {t("kiosk.failure.retry_after", { seconds: retryWaitSeconds })}
        </p>
      )}

      {classification === "internal" && supportMailtoHref !== undefined && (
        <div className="mt-6 flex flex-col items-center gap-2 text-center">
          {traceId !== undefined && (
            <p className="m-0 font-sans text-[14px] text-[var(--fg-muted)]">
              {t("kiosk.failure.support_reference", { traceId })}
            </p>
          )}
          <a
            href={supportMailtoHref}
            className="font-sans text-[15px] font-medium text-[var(--accent)] underline decoration-[var(--accent)] underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
          >
            {t("kiosk.failure.contact_support")}
          </a>
        </div>
      )}

      <p className="mt-[22px] font-sans text-[14px] text-[var(--fg-muted)]">
        {t("kiosk.success.auto_return", { seconds: autoReturnSeconds })}
      </p>
    </div>
  );
}
